import { Box, Loading, Typography } from '@iheartjane/reefer';
import { useEffect } from 'react';

import { MfaError } from './mfaError';
import { MfaForm } from './mfaForm';

export interface MfaProps {
  /** Auto-send code on mount by calling sendCode */
  autoSendCode?: boolean;
  /** Whether the code was resent */
  codeResent: boolean;
  /** Whether the MFA code has been sent successfully */
  codeSent: boolean;
  /** Contact display string: masked phone "( ••• ) ••• - 1234" or email */
  contactDisplay: string;
  /** Error message from sending the code, or null */
  errorSendingCode: string | null;
  /** Error message from verifying the code, or null */
  errorVerifyingCode: string | null;
  /** External loading state (e.g., widget loading) */
  isLoading?: boolean;
  /** Whether verifying a new phone number (changes header text) */
  newPhone?: boolean;
  /** Cancel MFA flow */
  onCancel?: () => void;
  /** Resend the MFA code */
  resendCode: () => void;
  /** Send the MFA code */
  sendCode: () => void;
  /** Whether a code is currently being sent */
  sendingCode: boolean;
  /** Show the cancel button */
  showCancelButton?: boolean;
  /** Whether to show "Resend Code" vs "Contact support" */
  showResendButton: boolean;
  /** Whether contact method is email (changes messaging) */
  usesEmail?: boolean;
  /** Verify the entered code */
  verifyCode: (code: string) => void;
}

export const Mfa = ({
  codeSent,
  codeResent,
  sendingCode,
  errorSendingCode,
  errorVerifyingCode,
  isLoading = false,
  autoSendCode = false,
  showResendButton,
  newPhone = false,
  contactDisplay,
  usesEmail = false,
  showCancelButton = true,
  sendCode,
  resendCode,
  verifyCode,
  onCancel,
}: MfaProps) => {
  useEffect(() => {
    if (autoSendCode) {
      sendCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSendCode]);

  const onSubmit = (values: { code: string }) => {
    verifyCode(values.code);
  };

  return (
    <Box>
      <Typography variant="header-bold" textAlign="center" mb={8} branded>
        Verify your {newPhone ? 'phone number' : 'identity'}
      </Typography>

      {(isLoading || sendingCode) && (
        <Box mt={24} py={48} position="relative">
          <Loading color="black" size="lg" />
        </Box>
      )}

      {!isLoading && !sendingCode && !errorSendingCode && codeSent && (
        <MfaForm
          contactDisplay={contactDisplay}
          errorVerifyingCode={errorVerifyingCode}
          usesEmail={usesEmail}
          onCancel={onCancel}
          onSubmit={onSubmit}
          resendCode={resendCode}
          showCancelButton={showCancelButton}
          showResendButton={showResendButton}
        />
      )}

      {!isLoading && !sendingCode && errorSendingCode && (
        <MfaError
          codeResent={codeResent}
          onCancel={onCancel}
          resendCode={resendCode}
          showCancelButton={showCancelButton}
          showResendButton={showResendButton}
        />
      )}
    </Box>
  );
};
